// src/pages/ProjectsPage.jsx
import { useEffect, useState } from "react";
import { X, Loader2, Plus, Trash2, ChevronRight } from "lucide-react";
import { useProjectStore } from "../stores/useProjectStore";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useNavigate } from "@tanstack/react-router";
import { PROJECT_TYPES } from "../constants/projectTypes";

export default function ProjectsPage() {
  const projects = useProjectStore(s => s.projects);
  const loadProjects = useProjectStore(s => s.loadProjects);
  const setActiveProjectId = useProjectStore(s => s.setActiveProjectId);

  const [showCreate, setShowCreate] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null); // { id, name }

  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <div className="p-6 space-y-4 max-w-4xl">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-gray-200">Projects</h1>
          <p className="text-xs text-fg-secondary mt-0.5">
            Select a project to activate it in Map Explorer.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md
            bg-accent-action/20 border border-accent-action/40
            text-accent-action hover:bg-accent-action/30 transition"
        >
          <Plus size={12} />
          New Project
        </button>
      </div>

      <div className="border border-slate-700 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-soft text-gray-400">
            <tr>
              <th className="text-left px-3 py-2 text-xs">Name</th>
              <th className="text-left px-3 py-2 text-xs">Slug</th>
              <th className="text-left px-3 py-2 text-xs">Role</th>
              <th className="text-left px-3 py-2 text-xs">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {projects.map(p => (
              <tr
                key={p.id}
                className="border-t border-slate-700 hover:bg-bg-soft/50 transition cursor-pointer"
                onClick={() => navigate({ to: '/projects/$projectId', params: { projectId: p.id } })}              >
                <td className="px-3 py-2 text-gray-200 font-medium">{p.name}</td>
                <td className="px-3 py-2 text-gray-400 font-mono text-xs">{p.slug}</td>
                <td className="px-3 py-2 text-gray-300 text-xs">{p.role ?? "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-mono uppercase tracking-wide
                    ${p.status === "active"
                      ? "bg-green-500/15 text-green-400"
                      : "bg-slate-600/20 text-slate-400"}`}>
                    {p.status}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setActiveProjectId(p.id); }}
                      className="text-[10px] text-accent-action hover:text-accent-action/80
                        border border-accent-action/30 rounded px-2 py-0.5 transition
                        hover:bg-accent-action/10"
                    >
                      Activate
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setProjectToDelete({ id: p.id, name: p.name }); }}
                      className="text-fg-secondary hover:text-red-400 transition p-0.5 rounded
                        hover:bg-red-400/10"
                      title="Delete project"
                    >
                      <Trash2 size={13} />
                    </button>
                    <ChevronRight size={14} className="text-fg-secondary" />
                  </div>
                </td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-fg-secondary text-xs italic">
                  No projects found. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Create Project Modal ── */}
      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            loadProjects();
          }}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {projectToDelete && (
        <DeleteProjectModal
          project={projectToDelete}
          onClose={() => setProjectToDelete(null)}
          onDeleted={() => {
            setProjectToDelete(null);
            loadProjects();
          }}
        />
      )}
    </div>
  );
}

/* ── DeleteProjectModal ─────────────────────────────────────────────────── */

function DeleteProjectModal({ project, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  async function handleDelete() {
    setDeleting(true);
    setError("");

    try {
      const res = await fetchWithAuth(`/api/v1/admin/projects/${project.id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }

      onDeleted();
    } catch (err) {
      setError(err.message || "Failed to delete project.");
      setDeleting(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-sm bg-bg-soft border border-slate-700 rounded-xl shadow-2xl p-6"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-500/15">
              <Trash2 size={14} className="text-red-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-100">Delete project</h2>
              <p className="text-xs text-fg-secondary mt-0.5">This action cannot be undone.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-fg-secondary hover:text-slate-300 transition"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <p className="text-xs text-slate-300 leading-relaxed mb-5">
          You are about to permanently delete{" "}
          <span className="font-semibold text-gray-100">{project.name}</span>.
          All members, invites, and AOIs associated with this project will be removed.
        </p>

        {error && (
          <p className="mb-4 text-xs text-red-400">{error}</p>
        )}

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            disabled={deleting}
            className="px-3 py-1.5 text-xs rounded-md border border-slate-700
              text-slate-400 hover:text-slate-200 hover:border-slate-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-md
              font-medium transition
              ${deleting
                ? "bg-red-500/10 border border-red-500/20 text-red-400/40 cursor-not-allowed"
                : "bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30"}`}
          >
            {deleting && <Loader2 size={12} className="animate-spin" />}
            {deleting ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── CreateProjectModal ─────────────────────────────────────────────────── */

function CreateProjectModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [type, setType] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function handleNameChange(v) {
    setName(v);
    if (!slugTouched) {
      setSlug(v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
    }
  }

  async function handleSubmit() {
    const trimName = name.trim();
    const trimSlug = slug.trim();

    if (!trimName) { setError("Project name is required."); return; }
    if (!trimSlug) { setError("Slug is required."); return; }
    if (!/^[a-z0-9_][a-z0-9_-]{2,62}$/.test(trimSlug)) {
      setError("Slug must be 3–63 chars: lowercase letters, numbers, hyphens, underscores.");
      return;
    }
    if (!type) { setError("Project type is required."); return; }

    setSubmitting(true);
    setError("");

    try {
      const res = await fetchWithAuth("/api/v1/admin/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: trimName,
          slug: trimSlug,
          description: description.trim() || null,
          type,
          status: "active",
        }),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }

      onCreated();
    } catch (err) {
      setError(err.message || "Failed to create project.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Escape") onClose();
    if (e.key === "Enter" && !submitting) handleSubmit();
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl p-6"
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-sm font-semibold text-gray-100">New Project</h2>
            <p className="text-xs text-fg-secondary mt-0.5">
              Projects scope layers, AOIs, and team access.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-fg-secondary hover:text-slate-300 transition"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="Project Name" required>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              placeholder="e.g. Delaware Basin Q3"
              maxLength={80}
              className={inputCls(!!error && !name.trim())}
            />
          </Field>

          <Field label="Slug" hint="URL-safe identifier — auto-generated from name">
            <input
              type="text"
              value={slug}
              onChange={e => { setSlug(e.target.value); setSlugTouched(true); }}
              placeholder="delaware-basin-q3"
              maxLength={60}
              className={inputCls(!!error && !slug.trim())}
            />
          </Field>

          <Field label="Type" hint="Purpose / lifecycle phase" required>
            <select
              value={type}
              onChange={e => setType(e.target.value)}
              className={inputCls(!!error && !type)}
            >
              <option value="" disabled>Select a type…</option>
              {PROJECT_TYPES.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Description" hint="Optional">
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of this project's scope…"
              rows={2}
              maxLength={300}
              className={inputCls(false) + " resize-none"}
            />
          </Field>
        </div>

        {error && (
          <p className="mt-3 text-xs text-red-400">{error}</p>
        )}

        <div className="flex gap-2 mt-5 justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded-md border border-slate-700
              text-slate-400 hover:text-slate-200 hover:border-slate-600 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !name.trim() || !type}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-md
              font-medium transition
              ${submitting || !name.trim() || !type
                ? "bg-accent-action/10 border border-accent-action/20 text-accent-action/40 cursor-not-allowed"
                : "bg-accent-action/20 border border-accent-action/50 text-accent-action hover:bg-accent-action/30"}`}
          >
            {submitting && <Loader2 size={12} className="animate-spin" />}
            {submitting ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function Field({ label, hint, required, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-semibold font-mono uppercase tracking-wider text-fg-secondary">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
        {hint && <span className="ml-1.5 normal-case font-normal text-fg-secondary">— {hint}</span>}
      </label>
      {children}
    </div>
  );
}

function inputCls(hasError) {
  return [
    "w-full rounded-md bg-bg px-3 py-2 text-xs text-white",
    "placeholder-slate-600 outline-none border transition-all",
    hasError
      ? "border-red-500/60 focus:ring-1 focus:ring-red-500/30"
      : "border-slate-700 focus:border-accent-action/50 focus:ring-1 focus:ring-accent-action/20",
  ].join(" ");
}

